import { OldPreset } from "../models/OldPreset";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { Deserializer } from "jsonapi-serializer";
import { genericError } from "../models/GenericError";
import { buildPreset } from "./presetService";
import { DynamicFieldDescriptor } from "../models/DynamicFieldDescriptor";
import { INTERNAL_SERVER_ERROR } from "../constants/response-status-codes";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import PresetCustomField from "../models/PresetCustomField";
import { ListQuery } from "../models/ListQuery";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { BIO_TNA_BTN_CONFIG_SETTINGS } from "../constants/biometric-fields-names";
import { Translator } from "../contexts/translator";

export const apiConnector = new APIConnector();

export const getPresetInputsByProfileId = async (profileId: string, translate: Translator): Promise<any> => {
    try {
        let includes = "include=profile,settingDatas.element.uiType,";
        includes += "settingDatas.element.elementAttributes,settingType";
        const sortOrder = "sort=settingType.stOrdinal";
        const fixedpagination = "page[totals]=&page[size]=100";
        const filter = `filter=profile.id==${profileId}`;
        const settingFields = "fields[setting]=settingDatas";
        const settingDataFields = "fields[settingData]=element,sdValstring";
        let elementFields = "fields[element]=elementAttributes,settingType,trnName,";
        elementFields += "eHint,uiType,eOrdinal,eRequired,eSysname,eUnits,eMax,eMin";
        const elementAttributeFields = "fields[elementAttribute]=trnName,eaValstring";
        const settingTypeFields = "fields[settingType]=stOrdinal";
        let url = `setting?${includes}&${sortOrder}&${fixedpagination}&${filter}&`;
        url += `${settingFields}&${settingDataFields}&${elementFields}&`;
        url += `${elementAttributeFields}&${settingTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail)})
            }

            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const builtPresetList = [] as any;
                const elements = [] as any;
                let builtCustomData = Object as any;
                data.map(async (apiPreset) => {
                    const settingDatas = apiPreset['setting-datas'];
                    const buildElementList = [] as any;
                    settingDatas.map((setttingData) => {
                        buildElementList.push(setttingData['element']);
                        elements.push(setttingData['element']);
                    });
                    const preset: PresetCustomField = buildPreset(apiPreset, translate, apiPreset['setting-datas'], buildElementList);
                    builtCustomData = { ...builtCustomData, ...preset.custom };
                    builtPresetList.push(preset);
                });

                const fieldDescriptors: DynamicFieldDescriptor[] = elements.map((apiElement): DynamicFieldDescriptor => {
                    const options = apiElement['element-attributes']
                        ? apiElement['element-attributes'].map(elementAttribute => {
                            return { name: elementAttribute['trn-name'], id: elementAttribute['ea-valstring'] }
                        })
                        : [];

                    const hasApiElement = (apiElement['setting-type'] != null);
                    return {
                        id: apiElement['id'],
                        displayName: apiElement['trn-name'],
                        toolTip: apiElement['e-hint'],
                        fieldTypeId: apiElement['ui-type']['id'],
                        ordinal: apiElement['e-ordinal'],
                        isRequired: apiElement['e-required'] === null ? false : apiElement['e-required'],//apiElement['mdt-required'] === 1,
                        systemName: apiElement['e-sysname'],
                        unit: apiElement['e-units'],
                        max: apiElement['e-max'],
                        min: apiElement['e-min'],
                        settingTypeOrdinal: !hasApiElement ? 0 : apiElement['setting-type']['st-ordinal'],
                        options: options
                    }
                });
                const groupElementFields = fieldDescriptors.sort((a, b) => a.ordinal - b.ordinal);

                return resolve({ rows: builtPresetList, pagination, customData: builtCustomData, elementFields: groupElementFields });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getPresetInputsForBiometricSettings = async (profileId: string, innertabFields: string[], translate: Translator): Promise<any> => {
    try {
        const includes = 'include=profile,settingDatas.element.uiType,settingDatas.element.elementAttributes,settingType'
        const settingField = 'fields[setting]=settingDatas'
        const settingDatasFields = 'fields[settingData]=element'
        const elementFields = 'fields[element]=uiType,elementAttributes,trnName,eHint,eOrdinal,eRequired,eSysname,eUnits,eMax,eMin,settingType'
        const elementAttributeFields = 'fields[elementAttribute]=trnName,eaValstring'
        const settingTypeFields = 'fields[settingType]=stOrdinal'
        const tabFieldsQuery = innertabFields.map(field => encodeURIComponent(field)).join(',');
        const filter = `filter=profile.id==${profileId};settingType.stSysname=in=(${tabFieldsQuery})`
        const sort = 'sort=settingType.stOrdinal'
        const url = `setting?${includes}&${filter}&${sort}&${settingField}&${settingDatasFields}&${elementFields}&${settingTypeFields}&${elementAttributeFields}`;        
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const builtPresetList = [] as any;
                const elements = [] as any;
                let builtCustomData = Object as any;
                data.map(async (apiPreset) => {
                    const settingDatas = apiPreset['setting-datas'];
                    const buildElementList = [] as any;
                    settingDatas.map((setttingData) => {
                        buildElementList.push(setttingData['element']);
                        elements.push(setttingData['element']);
                    });
                    const preset: PresetCustomField = buildPreset(apiPreset, translate, apiPreset['setting-datas'], buildElementList);
                    builtCustomData = { ...builtCustomData, ...preset.custom };
                    builtPresetList.push(preset);
                });

                const fieldDescriptors: DynamicFieldDescriptor[] = elements.map((apiElement): DynamicFieldDescriptor => {
                    const options = apiElement['element-attributes']
                        ? apiElement['element-attributes'].map(elementAttribute => {
                            return { name: elementAttribute['trn-name'], id: elementAttribute['ea-valstring'] }
                        })
                        : [];

                    const hasApiElement = (apiElement['setting-type'] != null);
                    return {
                        id: apiElement['id'],
                        displayName: apiElement['trn-name'],
                        toolTip: apiElement['e-hint'],
                        fieldTypeId: apiElement['ui-type']['id'],
                        ordinal: apiElement['e-ordinal'],
                        isRequired: apiElement['e-required'] === null ? false : apiElement['e-required'],//apiElement['mdt-required'] === 1,
                        systemName: apiElement['e-sysname'],
                        unit: apiElement['e-units'],
                        max: apiElement['e-max'],
                        min: apiElement['e-min'],
                        settingTypeOrdinal: !hasApiElement ? 0 : apiElement['setting-type']['st-ordinal'],
                        options: options
                    }
                });
                const groupElementFields = fieldDescriptors.sort((a, b) => a.ordinal - b.ordinal);

                return resolve({ rows: builtPresetList, pagination, customData: builtCustomData, elementFields: groupElementFields });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}


export const getPresetInputsForIdemiaThreatLevels = async (profileId: string, threatLevel: string, translate: Translator): Promise<any> => {
    try {
        let includes = "include=profile,settingDatas.element.uiType,";
        includes += "settingDatas.element.elementAttributes,settingType";
        const filter = `filter=profile.id==${profileId};settingType.stSysname=ini=${threatLevel}`;
        const sortOrder = "sort=settingType.stOrdinal";
        const settingFields = "fields[setting]=settingDatas,settingType,trnName";
        const settingDataFields = "fields[settingData]=element";
        let elementFields = "fields[element]=elementAttributes,trnName,eHint,uiType,";
        elementFields += "eOrdinal,eRequired,eSysname,eUnits,eMax,eMin,settingType";
        const elementAttributeFields = "fields[elementAttribute]=eaValstring,trnName";
        const settingTypeFields = "fields[settingType]=stOrdinal";
        let url = `setting?${includes}&${filter}&${sortOrder}&${settingFields}&`;
        url += `${settingDataFields}&${elementFields}&${elementAttributeFields}&`;
        url += `${settingTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const builtPresetList = [] as any;
                const elements = [] as any;
                let builtCustomData = Object as any;
                data.map(async (apiPreset) => {
                    const settingDatas = apiPreset['setting-datas'];
                    const buildElementList = [] as any;
                    settingDatas.map((setttingData) => {
                        buildElementList.push(setttingData['element']);
                        elements.push(setttingData['element']);
                    });
                    const preset: PresetCustomField = buildPreset(apiPreset, translate, apiPreset['setting-datas'], buildElementList);
                    builtCustomData = { ...builtCustomData, ...preset.custom };
                    builtPresetList.push(preset);
                });

                const fieldDescriptors: DynamicFieldDescriptor[] = elements.map((apiElement): DynamicFieldDescriptor => {
                    const options = apiElement['element-attributes']
                        ? apiElement['element-attributes'].map(elementAttribute => {
                            return { name: elementAttribute['trn-name'], id: elementAttribute['ea-valstring'] }
                        })
                        : [];

                    const hasApiElement = (apiElement['setting-type'] != null);
                    return {
                        id: apiElement['id'],
                        displayName: apiElement['trn-name'],
                        toolTip: apiElement['e-hint'],
                        fieldTypeId: apiElement['ui-type']['id'],
                        ordinal: apiElement['e-ordinal'],
                        isRequired: apiElement['e-required'] === null ? false : apiElement['e-required'],//apiElement['mdt-required'] === 1,
                        systemName: apiElement['e-sysname'],
                        unit: apiElement['e-units'],
                        max: apiElement['e-max'],
                        min: apiElement['e-min'],
                        settingTypeOrdinal: !hasApiElement ? 0 : apiElement['setting-type']['st-ordinal'],
                        options: options
                    }
                });
                const groupElementFields = fieldDescriptors.sort((a, b) => a.ordinal - b.ordinal);

                return resolve({ rows: builtPresetList, pagination, customData: builtCustomData, elementFields: groupElementFields });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getIdemiaButtonConfigurationForTimeAttendance = async (profileId: string, listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        let url = `setting?include=profile,settingType&sort=settingType.stOrdinal${partialURL}`;
        const staticFilters = `profile.id==${profileId};settingType.stSysname=ini=${BIO_TNA_BTN_CONFIG_SETTINGS}*`;

        if (url.includes('&filter')) {
            url = url + ';' + staticFilters;
        }
        else {
            url = url + '&filter=' + staticFilters;
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, async (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const buttons = data.map(button => {
                    return {
                        id: button['id'],
                        name: button['trn-name'],
                        nameOriginal:button['trn-name'],
                        type: button['setting-type']?.['trn-name'],
                        typeId: button['setting-type']?.['id'],
                        profileId: button['profile']?.['id'],
                }})

                return resolve({ rows: buttons, pagination: pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const updateButtonData = async (buttonData:any): Promise<any> => {
    try {
        const newRecord = {
            data: {
                type: "setting",
                id: buttonData.id,
                attributes: {
                    trnName: buttonData.buttonText,
                },
                relationships: {
                    settingType: {
                        data: {
                            type: "settingType",
                            id: buttonData.typeId
                        }
                    },
                    profile: {
                        data: {
                            type: "profile",
                            id: buttonData.profileId
                        }
                    }
                }
            }
        }

        const url = `setting/${newRecord.data.id}`;
        const settingResponse = await apiConnector.sendData(url, false, newRecord)

        return new Promise(async (resolve, reject) => {
            if (settingResponse) {
                let settingDataResponses: Response[] = new Array(0);
                return resolve(settingDataResponses)
            }
            else {
                return reject({ status: INTERNAL_SERVER_ERROR, id: buttonData.id, success: false });
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
